import { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import {
  getConversacionesAsistente,
  getMensajesAsistente,
  createConversacionAsistente,
  updateConversacionTitulo,
  deleteConversacionAsistente,
  derivarConversacionAsesor,
  getClienteIdByUserId,
  getClienteById,
} from '@/lib/supabaseService';

interface Mensaje {
  id: string;
  rol: 'usuario' | 'asistente';
  contenido: string;
  timestamp: Date;
  derivadoAsesor?: boolean;
  metadatos?: any;
}

interface Conversacion {
  id: string;
  titulo: string;
  fecha: Date;
  mensajes: Mensaje[];
}

export default function ClienteAsistente() {
  const { user } = useAuth();
  const router = useRouter();

  const [conversaciones, setConversaciones] = useState<Conversacion[]>([]);
  const [conversacionActiva, setConversacionActiva] = useState<Conversacion | null>(null);
  const [mensaje, setMensaje] = useState('');
  const [esperandoRespuesta, setEsperandoRespuesta] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [clienteId, setClienteId] = useState<string>('');
  const [accionesPendientes, setAccionesPendientes] = useState<any[]>([]);
  const [editandoConversacionId, setEditandoConversacionId] = useState<string | null>(null);
  const [nuevoTitulo, setNuevoTitulo] = useState<string>('');
  const [mostrandoSidebar, setMostrandoSidebar] = useState(false);
  const [conversacionAEliminar, setConversacionAEliminar] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const preguntasSugeridas = [
    '¿Cuál es el estado de mis documentos?',
    '¿Tengo incidencias abiertas?',
    '¿Qué facturas están pendientes de pago?',
    'Explícame qué es el IVA trimestral',
    '¿Cuáles son los plazos fiscales más importantes?',
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversacionActiva?.mensajes]);

  const loadClienteData = useCallback(async () => {
    try {
      setCargando(true);
      const cId = await getClienteIdByUserId(user!.id);
      if (!cId) {
        console.error('No client found for user');
        return;
      }
      setClienteId(cId);

      const convs = await getConversacionesAsistente(cId);
      const conversacionesConMensajes = await Promise.all(
        convs.map(async (conv) => {
          const mensajes = await getMensajesAsistente(conv.id);
          return {
            id: conv.id,
            titulo: conv.titulo,
            fecha: conv.fechaCreacion,
            mensajes: mensajes.map(m => ({
              id: m.id,
              rol: m.rol,
              contenido: m.contenido,
              timestamp: m.fecha,
              derivadoAsesor: m.esDerivacion,
              metadatos: m.metadatos,
            })),
          };
        })
      );
      setConversaciones(conversacionesConMensajes);
      if (conversacionesConMensajes.length > 0) {
        setConversacionActiva(conversacionesConMensajes[0]);
      }
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setCargando(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) loadClienteData();
  }, [user, loadClienteData]);

  const handleEnviarMensaje = async (e: React.FormEvent | null, mensajeOverride?: string) => {
    if (e) e.preventDefault();
    const textoMensaje = mensajeOverride || mensaje;
    if (!textoMensaje.trim() || !clienteId) return;

    setMensaje('');
    setEsperandoRespuesta(true);
    setAccionesPendientes([]);

    const nuevoMensaje: Mensaje = {
      id: Date.now().toString(),
      rol: 'usuario',
      contenido: textoMensaje,
      timestamp: new Date(),
    };

    if (conversacionActiva) {
      const conversacionActualizada = {
        ...conversacionActiva,
        mensajes: [...conversacionActiva.mensajes, nuevoMensaje],
      };
      setConversacionActiva(conversacionActualizada);
      setConversaciones(prev => prev.map(c =>
        c.id === conversacionActiva.id ? conversacionActualizada : c
      ));
    }

    try {
      const response = await fetch('/api/assistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clienteId,
          conversacionId: conversacionActiva?.id,
          mensaje: textoMensaje,
          userId: user!.id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const respuestaIA: Mensaje = {
          id: Date.now().toString(),
          rol: 'asistente',
          contenido: data.respuesta,
          timestamp: new Date(),
          metadatos: data.metadatos,
        };

        if (data.acciones) {
          setAccionesPendientes(data.acciones);
        }

        if (!conversacionActiva) {
          await loadClienteData();
        } else {
          setConversacionActiva(prev => {
            if (!prev) return null;
            const actualizada = { ...prev, mensajes: [...prev.mensajes, respuestaIA] };
            setConversaciones(prevConv => prevConv.map(c => c.id === prev.id ? actualizada : c));
            return actualizada;
          });
        }
      } else {
        const errorMsg: Mensaje = {
          id: Date.now().toString(),
          rol: 'asistente',
          contenido: 'Lo siento, ha ocurrido un error. Por favor, intenta de nuevo o contacta con tu asesor.',
          timestamp: new Date(),
        };
        if (conversacionActiva) {
          setConversacionActiva(prev => prev ? { ...prev, mensajes: [...prev.mensajes, errorMsg] } : null);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg: Mensaje = {
        id: Date.now().toString(),
        rol: 'asistente',
        contenido: 'Error de conexión. Por favor, verifica tu conexión a internet e intenta de nuevo.',
        timestamp: new Date(),
      };
      if (conversacionActiva) {
        setConversacionActiva(prev => prev ? { ...prev, mensajes: [...prev.mensajes, errorMsg] } : null);
      }
    } finally {
      setEsperandoRespuesta(false);
    }
  };

  const handlePreguntaSugerida = (pregunta: string) => {
    handleEnviarMensaje(null, pregunta);
  };

  const handleDerivarAsesor = async () => {
    if (!conversacionActiva || !clienteId) return;
    try {
      const cId = await getClienteIdByUserId(user!.id);
      if (!cId) { alert('Error al obtener datos del cliente.'); return; }
      const cliente = await getClienteById(cId);
      if (!cliente || !cliente.asesoresAsignados || cliente.asesoresAsignados.length === 0) {
        alert('No tienes un asesor asignado actualmente.');
        return;
      }
      const asesorId = cliente.asesoresAsignados[0];
      await derivarConversacionAsesor(conversacionActiva.id, asesorId, clienteId);
      const mensajeDerivar: Mensaje = {
        id: Date.now().toString(),
        rol: 'asistente',
        contenido: 'He enviado tu consulta a tu asesor. Te responderá en breve a través del módulo de Incidencias.',
        timestamp: new Date(),
        derivadoAsesor: true,
      };
      setConversacionActiva(prev => {
        if (!prev) return null;
        const actualizada = { ...prev, mensajes: [...prev.mensajes, mensajeDerivar] };
        setConversaciones(prevConv => prevConv.map(c => c.id === prev.id ? actualizada : c));
        return actualizada;
      });
      setTimeout(() => { router.push('/cliente/incidencias'); }, 2000);
    } catch (error) {
      console.error('Error escalating to advisor:', error);
      alert('Error al derivar la consulta. Por favor, intenta de nuevo.');
    }
  };

  const handleAccion = (accion: any) => {
    if (accion.tipo === 'navegacion') router.push(accion.url);
    else if (accion.tipo === 'derivar') handleDerivarAsesor();
  };

  const handleNuevaConversacion = async () => {
    if (!clienteId) return;
    try {
      const nuevaId = await createConversacionAsistente(clienteId, 'Nueva consulta');
      const nueva: Conversacion = { id: nuevaId, titulo: 'Nueva consulta', fecha: new Date(), mensajes: [] };
      setConversaciones([nueva, ...conversaciones]);
      setConversacionActiva(nueva);
      setAccionesPendientes([]);
      setEditandoConversacionId(nuevaId);
      setNuevoTitulo('Nueva consulta');
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handleIniciarEdicion = (conversacion: Conversacion) => {
    setEditandoConversacionId(conversacion.id);
    setNuevoTitulo(conversacion.titulo);
  };

  const handleGuardarTitulo = async () => {
    if (!editandoConversacionId || !nuevoTitulo.trim()) return;
    try {
      await updateConversacionTitulo(editandoConversacionId, nuevoTitulo.trim());
      setConversaciones(prev => prev.map(c => c.id === editandoConversacionId ? { ...c, titulo: nuevoTitulo.trim() } : c));
      if (conversacionActiva?.id === editandoConversacionId) {
        setConversacionActiva(prev => prev ? { ...prev, titulo: nuevoTitulo.trim() } : null);
      }
      setEditandoConversacionId(null);
      setNuevoTitulo('');
    } catch (error) {
      console.error('Error updating title:', error);
    }
  };

  const handleCancelarEdicion = () => {
    setEditandoConversacionId(null);
    setNuevoTitulo('');
  };

  const handleEliminarConversacion = (conversacionId: string) => {
    setConversacionAEliminar(conversacionId);
  };

  const confirmarEliminarConversacion = async () => {
    if (!conversacionAEliminar) return;
    try {
      await deleteConversacionAsistente(conversacionAEliminar);
      setConversaciones(prev => prev.filter(c => c.id !== conversacionAEliminar));
      if (conversacionActiva?.id === conversacionAEliminar) {
        const remaining = conversaciones.filter(c => c.id !== conversacionAEliminar);
        setConversacionActiva(remaining.length > 0 ? remaining[0] : null);
      }
      setConversacionAEliminar(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setConversacionAEliminar(null);
    }
  };

  if (cargando) {
    return (
      <Layout rol="cliente">
        <div className="h-[calc(100vh-72px)] flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-4xl text-calm-muted">progress_activity</span>
            <p className="text-calm-muted font-light mt-2">Cargando asistente...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout rol="cliente">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setMostrandoSidebar(!mostrandoSidebar)}
        className="md:hidden fixed top-20 left-4 z-50 btn btn-cliente p-2"
      >
        <span className="material-symbols-outlined text-[18px]">
          {mostrandoSidebar ? 'close' : 'menu'}
        </span>
      </button>

      <div className="h-[calc(100vh-72px)] flex gap-6 p-4 md:p-8">

        {/* ── Sidebar ── */}
        <div className={`
          fixed md:relative inset-0 md:inset-auto z-40 md:z-auto
          bg-white/95 backdrop-blur-xl md:bg-transparent md:backdrop-blur-none
          w-full md:w-80 md:flex-shrink-0
          flex flex-col space-y-4
          p-4 md:p-0
          transition-transform duration-300 ease-in-out
          ${mostrandoSidebar ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}>
          <button
            onClick={() => { handleNuevaConversacion(); setMostrandoSidebar(false); }}
            className="btn btn-cliente w-full"
          >
            <span className="material-symbols-outlined text-sm align-middle">add</span> Nueva Consulta
          </button>

          <div className="card h-[calc(100%-4rem)] overflow-y-auto">
            <h3 className="font-light text-calm-text mb-3">Historial</h3>
            {conversaciones.length === 0 ? (
              <div className="text-center py-10">
                <span className="material-symbols-outlined text-3xl text-calm-muted mb-2 block">forum</span>
                <p className="text-xs text-calm-muted font-light">Sin consultas aún.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversaciones.map(conv => (
                  <div key={conv.id} className="relative group">
                    {editandoConversacionId === conv.id ? (
                      <div className="p-2 space-y-2">
                        <input
                          type="text"
                          value={nuevoTitulo}
                          onChange={(e) => setNuevoTitulo(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleGuardarTitulo();
                            else if (e.key === 'Escape') handleCancelarEdicion();
                          }}
                          className="input w-full text-sm py-1.5"
                          autoFocus
                        />
                        <div className="flex gap-1.5">
                          <button onClick={handleGuardarTitulo} className="btn btn-cliente btn-sm flex-1">
                            <span className="material-symbols-outlined text-[13px]">check</span> Guardar
                          </button>
                          <button onClick={handleCancelarEdicion} className="btn btn-ghost btn-sm flex-1 border border-calm-border">
                            <span className="material-symbols-outlined text-[13px]">close</span> Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setConversacionActiva(conv); setMostrandoSidebar(false); setAccionesPendientes([]); }}
                        className={`w-full text-left p-3 rounded-huge transition-colors ${
                          conversacionActiva?.id === conv.id
                            ? 'bg-fresh-sky-50 border-2 border-fresh-sky-400'
                            : 'bg-calm-bg hover:bg-calm-bg/80 border-2 border-transparent'
                        }`}
                      >
                        <div className="font-medium text-sm text-calm-text truncate pr-8">
                          {conv.titulo}
                          {conv.mensajes.length > 0 && (
                            <span className="ml-1.5 text-[10px] bg-fresh-sky-100 text-fresh-sky-600 rounded-full px-1.5 py-0.5">
                              {conv.mensajes.length}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-calm-muted mt-1">{conv.fecha.toLocaleDateString('es-ES')}</div>
                        {conv.mensajes.length > 0 && (
                          <p className="text-[11px] text-calm-muted/50 mt-0.5 font-light truncate">
                            {conv.mensajes[conv.mensajes.length - 1].contenido.substring(0, 50)}…
                          </p>
                        )}
                      </button>
                    )}
                    {editandoConversacionId !== conv.id && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleIniciarEdicion(conv); }}
                          className="p-1 rounded-lg text-calm-muted hover:text-calm-text hover:bg-calm-border transition-colors"
                          title="Renombrar"
                        >
                          <span className="material-symbols-outlined text-[14px]">edit</span>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEliminarConversacion(conv.id); }}
                          className="p-1 rounded-lg text-calm-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Eliminar"
                        >
                          <span className="material-symbols-outlined text-[14px]">delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="card mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-5xl font-light font-display text-calm-text">Asistente E2</h1>
                <p className="text-calm-muted text-sm mt-1">
                  Respuestas rápidas sobre contabilidad y fiscalidad en lenguaje simple
                </p>
              </div>
              <span className="material-symbols-outlined text-4xl text-calm-accent">smart_toy</span>
            </div>
          </div>

          {/* Suggested questions */}
          {(!conversacionActiva || conversacionActiva.mensajes.length === 0) && (
            <div className="card mb-4">
              <h3 className="font-light text-calm-text mb-3">Preguntas sugeridas:</h3>
              <div className="grid grid-cols-1 gap-2">
                {preguntasSugeridas.map((pregunta, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePreguntaSugerida(pregunta)}
                    disabled={esperandoRespuesta}
                    className="text-left p-3 bg-fresh-sky-50/50 hover:bg-fresh-sky-50 rounded-huge text-sm text-calm-text transition-colors disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-sm align-middle mr-1 text-fresh-sky-500">chat</span>
                    {pregunta}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 card overflow-y-auto mb-4">
            <div className="space-y-4">
              {conversacionActiva?.mensajes.map(msg => (
                <div key={msg.id} className={`flex ${msg.rol === 'usuario' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-3xl rounded-huge p-4 ${
                    msg.rol === 'usuario'
                      ? 'bg-fresh-sky-600 text-white'
                      : msg.derivadoAsesor
                      ? 'bg-emerald-50 text-emerald-900 border-2 border-emerald-200'
                      : 'bg-calm-bg text-calm-text'
                  }`}>
                    <div className="whitespace-pre-wrap text-sm">{msg.contenido}</div>
                    {msg.derivadoAsesor && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 font-light">
                        <span className="material-symbols-outlined text-sm">person</span>
                        Derivado a tu asesor
                      </div>
                    )}
                    <div className={`text-xs mt-2 ${
                      msg.rol === 'usuario' ? 'text-white/70' : msg.derivadoAsesor ? 'text-emerald-600' : 'text-calm-muted'
                    }`}>
                      {msg.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}

              {esperandoRespuesta && (
                <div className="flex justify-start">
                  <div className="bg-calm-bg rounded-huge p-4">
                    <div className="flex items-center space-x-2">
                      <div className="animate-bounce">●</div>
                      <div className="animate-bounce" style={{ animationDelay: '0.2s' }}>●</div>
                      <div className="animate-bounce" style={{ animationDelay: '0.4s' }}>●</div>
                    </div>
                  </div>
                </div>
              )}

              {accionesPendientes.length > 0 && (
                <div className="flex justify-start">
                  <div className="space-y-2">
                    {accionesPendientes.map((accion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleAccion(accion)}
                        className="ghost-btn w-full text-sm"
                      >
                        {accion.etiqueta}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <form onSubmit={(e) => handleEnviarMensaje(e)} className="card">
            <div className="flex gap-3 items-center">
              {conversacionActiva && conversacionActiva.mensajes.length > 0 && !accionesPendientes.some(a => a.tipo === 'derivar') && (
                <button
                  type="button"
                  onClick={handleDerivarAsesor}
                  className="text-calm-muted hover:text-fresh-sky-600 transition-colors shrink-0"
                  title="Derivar al asesor"
                >
                  <span className="material-symbols-outlined">person</span>
                </button>
              )}
              <input
                type="text"
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Escribe tu pregunta..."
                className="input flex-1"
                disabled={esperandoRespuesta}
              />
              <button
                type="submit"
                className="btn btn-cliente"
                disabled={!mensaje.trim() || esperandoRespuesta}
              >
                Enviar
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {conversacionAEliminar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full">
            <h3 className="text-lg font-light text-calm-text mb-2">Eliminar consulta</h3>
            <p className="text-calm-muted mb-6 font-light">
              ¿Estás seguro de que quieres eliminar esta consulta? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConversacionAEliminar(null)}
                className="px-4 py-2 text-calm-text bg-calm-bg rounded-huge hover:bg-calm-border transition-colors font-light text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEliminarConversacion}
                className="btn btn-danger"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
